import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { buildV2HistoryReport, buildV2ReportDetailRows, getReportFilename } from "./v2ReportUtils";

const PRIMARY = [40, 89, 107];
const TEXT = [17, 24, 39];
const MUTED = [75, 85, 99];

export function exportV2HistoryPdf({
  logs = [],
  days = 14,
  selectedDate,
  profile = {},
  generatedAt = new Date(),
} = {}) {
  const report = buildV2HistoryReport({ logs, days, selectedDate, profile, generatedAt });
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();

  drawHeader(doc, report, pageWidth);
  drawSummaryTable(doc, report);
  drawFoodHistory(doc, report);
  drawPageNumbers(doc);

  doc.save(getReportFilename(report.range.days));
  return report;
}

function drawHeader(doc, report, pageWidth) {
  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, pageWidth, 62, "F");

  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.text(report.appName, 40, 30);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.text(report.title, 40, 48);

  doc.setTextColor(...TEXT);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(15);
  doc.text(report.range.days === 1 ? "Selected Day Report" : `${report.range.days}-Day Report`, 40, 88);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text(`Patient: ${report.patientName}`, 40, 108);
  doc.text(`Weight: ${formatWeight(report.weightKg)}`, 40, 124);
  doc.text(
    report.range.days === 1
      ? `Selected date: ${report.range.startDate}`
      : `Range: ${report.range.startDate} to ${report.range.endDate}`,
    260,
    108
  );
  doc.text(`Generated: ${formatDateTime(report.generatedAt)}`, 260, 124);
}

function drawSummaryTable(doc, report) {
  autoTable(doc, {
    startY: 148,
    head: [["Date", "Items", "Potassium", "Phosphorus", "Sodium", "Protein"]],
    body: report.dailyRows.map((row) => [
      row.date,
      row.itemCount,
      `${formatAmount(row.totals.potassium_mg)} mg`,
      `${formatAmount(row.totals.phosphorus_mg)} mg`,
      `${formatAmount(row.totals.sodium_mg)} mg`,
      `${formatAmount(row.totals.protein_g)} g`,
    ]),
    styles: { fontSize: 8, cellPadding: 4 },
    headStyles: { fillColor: PRIMARY, textColor: [255, 255, 255] },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    margin: { left: 40, right: 40 },
  });
}

function drawFoodHistory(doc, report) {
  const rows = buildV2ReportDetailRows(report);

  autoTable(doc, {
    startY: doc.lastAutoTable.finalY + 24,
    head: [["Date", "Time", "Food", "Portions eaten", "Serving", "Potassium", "Phosphorus", "Sodium", "Protein"]],
    body: rows,
    styles: { fontSize: 8, cellPadding: 4, overflow: "linebreak" },
    headStyles: { fillColor: PRIMARY, textColor: [255, 255, 255] },
    columnStyles: {
      2: { cellWidth: 115 },
      3: { cellWidth: 48 },
      4: { cellWidth: 68 },
    },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    margin: { left: 40, right: 40 },
  });
}

function drawPageNumbers(doc) {
  const pageCount = doc.internal.getNumberOfPages();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  for (let page = 1; page <= pageCount; page += 1) {
    doc.setPage(page);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(`Page ${page} of ${pageCount}`, pageWidth - 86, pageHeight - 24);
  }
}

function formatWeight(value) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? `${formatAmount(numeric)} kg` : "Not entered";
}

function formatDateTime(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString();
}

function formatAmount(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0";
  if (Number.isInteger(numeric)) return String(numeric);
  return numeric.toFixed(1);
}
